import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DSModal from '../../../design-system/DSModal';
import DSBox from '../../../design-system/DSBox';
import DSCheckbox from '../../../design-system/DSCheckBox';
import DSFormControlLabel from '../../../design-system/DSFormControlLabel';
import DSSecondaryButton from '../../../design-system/DSSecondaryButton';
import DSPrimaryButton from '../../../design-system/DSPrimaryButton';
import { DSTypography } from '../../../design-system/Typography';
import { PublicUnsupportedProperty } from '../../../utils/klydoValidation';

const COPY = {
  title: 'Some properties will be removed',
  subtitle:
    'The public feed does not play sound, so these are removed from this ' +
    'Klydo when it is approved:',
  footnote:
    'This cannot be undone — the Klydo has one version, so the sound is gone ' +
    'from your clock too. Everything else stays as it is.',
  consent: 'I understand these properties will be removed',
  confirm: 'Continue',
};

type SpecialPropertiesModalProps = {
  open: boolean;
  properties: PublicUnsupportedProperty[];
  onClose: () => void;
  onConfirm: () => void;
  /** Request in flight — keeps a second Continue from opening a second one. */
  isSubmitting?: boolean;
};

/**
 * Step ONE of the publication flow, shown only when the klydo carries a
 * property the feed cannot play (see `getPublicUnsupportedProperties`).
 *
 * Disclosure, not a gate: the api strips these fields when it approves the
 * request (`PublicKlydoSoundStripper`), with or without this dialog. What the
 * dialog buys is that the artist is not surprised by it — sealing is one-way,
 * so there is no undo once the request is approved.
 */
const SpecialPropertiesModal = ({
  open,
  properties,
  onClose,
  onConfirm,
  isSubmitting = false,
}: SpecialPropertiesModalProps) => {
  const { spacing, palette } = useTheme();
  const [consented, setConsented] = useState(false);

  // A fresh consent per opening: leaving it checked would let a second klydo
  // (the gallery renders one of these per card) inherit the first one's answer.
  useEffect(() => {
    if (!open) setConsented(false);
  }, [open]);

  return (
    <DSModal open={open} onClose={onClose}>
      <DSBox
        // The gallery renders this inside a card that navigates on click, and
        // React bubbles portal events through the REACT tree, not the DOM one —
        // so without this, ticking the checkbox routed to the klydo page and
        // the request was never sent.
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
        sx={{
          width: { xs: '90vw', sm: 440 },
          padding: spacing(3),
        }}
      >
        <DSTypography variant="h6" sx={{ marginBottom: spacing(0.5) }}>
          {COPY.title}
        </DSTypography>
        <DSTypography
          variant="body2"
          sx={{ color: palette.text.secondary, marginBottom: spacing(2) }}
        >
          {COPY.subtitle}
        </DSTypography>

        <DSBox
          component="ul"
          sx={{
            margin: 0,
            marginBottom: spacing(2),
            paddingLeft: spacing(3),
          }}
        >
          {properties.map((property) => (
            <DSBox component="li" key={property}>
              <DSTypography variant="body2">{property}</DSTypography>
            </DSBox>
          ))}
        </DSBox>

        <DSFormControlLabel
          control={
            <DSCheckbox
              checked={consented}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setConsented(event.target.checked)
              }
            />
          }
          label={<DSTypography variant="body2">{COPY.consent}</DSTypography>}
        />

        <DSBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing(1),
            marginTop: spacing(2),
          }}
        >
          <InfoOutlinedIcon
            sx={{ color: palette.text.secondary, fontSize: 18 }}
          />
          <DSTypography variant="body2" sx={{ color: palette.text.secondary }}>
            {COPY.footnote}
          </DSTypography>
        </DSBox>

        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: spacing(1.5),
            marginTop: spacing(3),
            paddingTop: spacing(2),
          }}
        >
          <DSSecondaryButton onClick={onClose} sx={{ paddingX: spacing(2) }}>
            Cancel
          </DSSecondaryButton>
          <DSPrimaryButton
            onClick={onConfirm}
            disabled={!consented || isSubmitting}
          >
            {COPY.confirm}
          </DSPrimaryButton>
        </DSBox>
      </DSBox>
    </DSModal>
  );
};

export default SpecialPropertiesModal;
